import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getViewAsRoleFromStorage, type ViewAsRole } from '../utils/roles';

export type ThemeMode = 'light' | 'dark';
export type Language = 'en' | 'es';
export type { ViewAsRole };

interface SettingsStore {
  theme: ThemeMode;
  language: Language;
  viewAsRole: ViewAsRole;
  sidebarCollapsed: boolean;
  setTheme: (theme: ThemeMode) => void;
  setLanguage: (language: Language) => void;
  setViewAsRole: (role: ViewAsRole) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleTheme: () => void;
}

function getSystemTheme(): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getSystemLanguage(): Language {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  return candidates.some((value) => value.toLowerCase().startsWith('es')) ? 'es' : 'en';
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      theme: getSystemTheme(),
      language: getSystemLanguage(),
      viewAsRole: 'admin',
      sidebarCollapsed: false,
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      setViewAsRole: (viewAsRole) => set({ viewAsRole }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleTheme: () =>
        set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
    }),
    {
      name: 'y-sports-settings',
      version: 3,
      migrate: (persistedState) => {
        const state = persistedState as Partial<SettingsStore> | undefined;
        return {
          ...state,
          viewAsRole: getViewAsRoleFromStorage(state?.viewAsRole),
          sidebarCollapsed: state?.sidebarCollapsed ?? false,
        };
      },
    }
  )
);
